import type { Location, Scan } from "@hobbyshopify/backend-client";
import { Rx, RxList } from "subtree.js/core";
import type { EditState } from "../../core/primitives/scan-edit";

export class ScanEditorState {
  /**
   * The scan as the last draft save described it. Editing is a round trip: a
   * correction can move the match, re-run the pricing lookup and change the
   * estimate, so this is what the screen reads, not the first fetch.
   */
  readonly scan = new Rx<Scan | null>(null);
  /** The form. Replaced on every change, never mutated. */
  readonly edit = new Rx<EditState | null>(null);
  /** A draft save is in flight. */
  readonly saving = new Rx(false);
  /** A draft save that failed. Never blocks confirming. */
  readonly draftError = new Rx<string | null>(null);
  /** The candidate a pick is in flight for, by product id. */
  readonly picking = new Rx<string | null>(null);
  /** A refused confirm or reject. */
  readonly error = new Rx<string | null>(null);
  /** A confirm or reject is in flight. */
  readonly busy = new Rx(false);
  /** Derived: pending, valid, nothing in flight. */
  readonly canConfirm = new Rx(false);
  /** Derived: why a pending scan cannot be confirmed, when a field is invalid. */
  readonly confirmBlocker = new Rx<string | null>(null);
  /** Step two of confirming is open. */
  readonly askingStock = new Rx(false);
  /** Which image the lightbox shows, or null. */
  readonly zoomIndex = new Rx<number | null>(null);
  /** The shop's branches, for the stock step. */
  readonly locations = new RxList<Location>();
  /** From the store: how long an approval quotes the price. */
  readonly quoteValidityMinutes = new Rx<number | null>(null);
}

export abstract class ScanEditorActions {
  abstract change(updates: Partial<EditState>): void;
  /** Make one of the scan's candidates the pick; the price follows it unless the reviewer typed one. */
  abstract pick(productId: string): Promise<void>;
  /** The primary button: opens step two, or confirms outright for a customer's scan. */
  abstract primary(): void;
  abstract confirm(stock?: { quantity: number; locationId?: string }): Promise<void>;
  abstract cancelStock(): void;
  abstract reject(): Promise<void>;
  abstract openZoom(index: number): void;
  abstract closeZoom(): void;
}
